import React, {useState, useContext, useEffect, useRef} from 'react'
import AppLayout from 'components/AppLayout/AppLayout';
import AppDrawer from 'components/AppDrawer/AppDrawer';
import Typography from '@mui/material/Typography';
import { DataContext } from 'contexts/DataContext';
import { Gantt, Task, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";
import EditableGrid from 'components/EditableGrid/EditableGrid';
import 'i18n/config';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid';

import Donut from './Donut';
import Add from '@mui/icons-material/Add';

import { get, update, destroy, getColumns, getIncrements } from './api'
import { getStatus } from 'pages/Assessment/api';
import './treatment.css';
import Detail from './Detail';
import { getCategories } from './api';

const Treatments = () => {
    const { t } = useTranslation();
    const dataContext = useContext(DataContext);
    const [tasks, setTasks] = useState([] as Task[]);
    const [selectedDate, setSelectedDate] = useState("");
    const addButtonRef = useRef(null);
    const [status, setStatus] = useState({} as any);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [increment, setIncrement] = useState({} as any);


    const [columns, setColumns] = useState([] as GridColDef[]);
    useEffect(() => {
      if(dataContext.loading) return;
      getColumns(dataContext.language).then((response) => {
        setColumns(response);
      });
    }, [dataContext.loading, dataContext.language]);

    useEffect(() => {
      if(dataContext.loading) return;
      if(!dataContext.currentOrganization) return;
      if(!dataContext.currentAssessment) return;
      dataContext.changeShowLoader(true);

      getStatus(dataContext.currentOrganization, dataContext.currentAssessment).then((response) => {
        setStatus(response);

        getIncrements(dataContext.currentAssessment).then((response) => {
          setIncrement(response);
          setDataLoaded(true)
        })
      });

      get(dataContext.currentAssessment).then((response) => {
        response = response.map((item:any)=>{
          item.start = new Date(item.start);
          item.end = new Date(item.end);
          item.type = "task";
          item.styles  = {};
          item.styles.progressColor = item.implementation == 1 ? '#e58909' : '#2b589b'
          item.styles.progressSelectedColor = item.implementation == 1 ? '#e58909' : '#2b589b'
          item.styles.backgroundColor = item.implementation == 1 ? '#FE9D0C' : '#3367b9'
          item.styles.backgroundSelectedColor = item.implementation == 1 ? '#FE9D0C' : '#3367b9'
          return item;
        })
        setTasks(response)
      })
     
      dataContext.changeShowLoader(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, dataContext.loading, dataContext.currentOrganization, dataContext.currentAssessment, dataContext.reloadParent]);


    useEffect(() => {
      if(!columns.length) return;
      getCategories().then((response:any) => {
        let col = columns.filter(column => column.field === "category")[0] as any;
        if(col) {
          col.valueOptions = response.map((category:any) => { return {value:category, label: category} });
          dataContext.changeShowLoader(false);
        }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns]);


    useEffect(()=>{
      //getRoles().then((response:any) => {
        let implementationCol = columns.filter(column => column.field === "implementation")[0] as any;
        if(implementationCol) {
          let implementation = [{id:1, name:t('One shot')},{id:2,name:t('Recurrent')}];
          implementationCol.valueOptions = implementation.map((role:any) => { return {value: role.id, label: role.name} });
        }

        let coverageCol = columns.filter(column => column.field === "coverage")[0] as any;
        if(coverageCol) {
          let coverage = [{id:1, name:t('Complete')},{id:2,name:t('Partial')}];
          coverageCol.valueOptions = coverage.map((role:any) => { return {value: role.id, label: role.name} });
        }

        let statusCol = columns.filter(column => column.field === "status")[0] as any;
        if(statusCol) {
          let status = [
            {id:1, name:t('Pending')},
            {id:2,name:t('Approved')},
            {id:3,name:t('Ongoing')},
            {id:4,name:t('Completed')},
            {id:5,name:t('Canceled')},
          ];

          statusCol.valueOptions = status.map((role:any) => { return {value: role.id, label: role.name} });
        }
      //});
    })

    function getStartEndDateForProject(tasks: Task[], projectId: string) {
      const projectTasks = tasks.filter(t => t.project === projectId);
      let start = projectTasks[0].start;
      let end = projectTasks[0].end;
    
      for (let i = 0; i < projectTasks?.length; i++) {
        const task = projectTasks[i];
        if (start.getTime() > task.start.getTime()) {
          start = task.start;
        }
        if (end.getTime() < task.end.getTime()) {
          end = task.end;
        }
      }
      return [start, end];
    }

    const handleTaskChange = (task: Task) => {
      console.log("On date change Id:" + task.id);
      dataContext.changeShowLoader(true);
      update(task, dataContext.currentAssessment).then((response)=>{
        dataContext.toggleReloadParent()
        dataContext.changeShowLoader(false);
      })

      let newTasks = tasks.map(t => (t.id === task.id ? task : t));
      if (task.project) {
        const [start, end] = getStartEndDateForProject(newTasks, task.project);
        const project = newTasks[newTasks.findIndex(t => t.id === task.project)];
        if (
          project.start.getTime() !== start.getTime() ||
          project.end.getTime() !== end.getTime()
        ) {
          const changedProject = { ...project, start, end };
          newTasks = newTasks.map(t =>
            t.id === task.project ? changedProject : t
          );
        }
      }
      setTasks(newTasks);
    };
  
    const handleTaskDelete = (task: Task) => {
      const conf = window.confirm("Are you sure about " + task.name + " ?");
      if (conf) {
        setTasks(tasks.filter(t => t.id !== task.id));
      }
      return conf;
    };
  
    const handleProgressChange = async (task: Task) => {
      setTasks(tasks.map(t => (t.id === task.id ? task : t)));
      console.log("On progress change Id:", task);
      dataContext.changeShowLoader(true);
      update(task, dataContext.currentAssessment).then((response)=>{
        dataContext.toggleReloadParent()
        dataContext.changeShowLoader(false);
      })
    };
  
    const handleDblClick = (task: Task) => {
      alert("On Double Click event Id:" + task.id);
    };
  
    const handleClick = (task: Task) => {
      console.log("On Click event Id:" + task.id);
    };
  
    const handleSelect = (task: Task, isSelected: boolean) => {
      console.log(task.name + " has " + (isSelected ? "selected" : "unselected"));
    };
  
    const handleExpanderClick = (task: Task) => {
      setTasks(tasks.map(t => (t.id === task.id ? task : t)));
      console.log("On expander click Id:" + task.id);
    };

    
    return (
      <>
      <AppLayout drawerOpen={true} drawer={<AppDrawer />} 
        title={<><Typography variant='pageTitle'>{t('Treatments')}</Typography></>}
        actions={[
          {
            label: t('Add treatment'),
            icon:<Add />,
            action: () => {
              let addbutton = addButtonRef.current as any;
              addbutton.click();
            }
          }
        ]}>

        {dataContext.currentAssessment && 
        <EditableGrid addButtonRef={addButtonRef} hideAddButton={true} columns={columns} 
        showGridToolbar={true}
        key={dataContext.currentAssessment}
        get={()=>get(dataContext.currentAssessment)}
        details={(props) => <Detail {...props} tasks={tasks} />}
        update={(row)=>{
          var item = row as Task;
          item.start = new Date(item.start);
          item.end = new Date(item.end);
          item.type = "task";
          item.styles  = {};
          item.styles.progressColor = row.implementation == 1 ? '#e58909' : '#2b589b'
          item.styles.progressSelectedColor = row.implementation == 1 ? '#e58909' : '#2b589b'
          item.styles.backgroundColor = row.implementation == 1 ? '#FE9D0C' : '#3367b9'
          item.styles.backgroundSelectedColor = row.implementation == 1 ? '#FE9D0C' : '#3367b9'
          setTasks(tasks.map(t => (t.id === item.id ? item : t)));
          return update(row, dataContext.currentAssessment)
        }}
        delete={destroy}
        reloadParentOnEditStop={true}
        name='organizationGrid'
        labels={{
          "actions": t('Actions'),
          "add": {
            "button": t('Add treatment')
          },
          "details": {
            "column":t('Details'), 
            "button":t('View')
          }
        }} />}


        </AppLayout>
      </>
    )

}

export default Treatments;